/**
 * Eric (2026-06-16 — iter107 SPEC STRICK ULTRA) — Page de diagnostic de
 * visibilité des parcours. Permet à un admin (Eric / Vincent) de saisir
 * un email d'utilisateur et voir EN UN COUP D'ŒIL quels parcours il
 * voit ou ne voit pas, avec la RAISON exacte (synonymes, target_groups
 * non matchés, parcours non publié, etc.).
 *
 * Cible : débloquer Étienne et tout futur bêta-testeur qui rapporte
 * "je ne vois pas mon parcours" — sans devoir consulter les logs serveur.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function VisibilityDiagnosticPage() {
  const { lang } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    if (!email.trim()) {
      toast.error(lang === "fr" ? "Saisis un email d'utilisateur" : "Enter a user email");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      // Étape 1 : résoudre l'email → user_id via /api/organizations/members?email=
      // Fallback : si l'endpoint n'existe pas, le backend matchera par email directement.
      // On essaie l'endpoint debug avec l'email comme user_id (le backend gère les deux).
      let userId = email.trim();
      // Si l'admin a saisi un email plutôt qu'un ID, on demande au backend de résoudre.
      // Pour ce premier shot, l'endpoint accepte uniquement l'ID — on appelle un helper.
      const lookupResp = await axios.get(
        `${API}/api/users/lookup-by-email?email=${encodeURIComponent(email.trim())}`,
        { withCredentials: true }
      ).catch(() => null);
      if (lookupResp?.data?.id) {
        userId = lookupResp.data.id;
      }
      const r = await axios.get(
        `${API}/api/pathways/debug-visibility/${userId}`,
        { withCredentials: true }
      );
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="visibility-diagnostic-page">
      <h1 className="text-2xl font-bold mb-2">
        🔍 {lang === "fr" ? "Diagnostic visibilité des parcours" : "Pathway visibility diagnostic"}
      </h1>
      <p className="text-sm text-zinc-600 mb-6">
        {lang === "fr"
          ? "Saisis l'email d'un utilisateur et clique « Diagnostiquer » pour voir EXACTEMENT quels parcours il voit, et pour chaque parcours non visible, la RAISON précise."
          : "Enter a user email and click 'Diagnose' to see EXACTLY which pathways they can see, and for each hidden pathway, the PRECISE reason."}
      </p>

      <div className="flex gap-2 mb-6">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && run()}
          placeholder="etienne@finance-libre.com"
          className="flex-1 px-3 py-2 border border-zinc-300 rounded text-sm"
          data-testid="visibility-diag-email-input"
        />
        <button
          onClick={run}
          disabled={loading}
          className="px-4 py-2 bg-[#002FA7] text-white text-sm font-semibold rounded hover:bg-[#001a6b] disabled:opacity-50"
          data-testid="visibility-diag-submit"
        >
          {loading ? (lang === "fr" ? "⏳ Analyse…" : "⏳ Running…") : (lang === "fr" ? "🔍 Diagnostiquer" : "🔍 Diagnose")}
        </button>
      </div>

      {error && (
        <div className="p-4 bg-rose-50 border border-rose-300 rounded text-rose-800 text-sm mb-6" data-testid="visibility-diag-error">
          ⚠️ {error}
        </div>
      )}

      {result && (
        <div className="space-y-4" data-testid="visibility-diag-result">
          {/* Carte utilisateur */}
          <div className="border border-zinc-200 rounded-xl p-4 bg-zinc-50">
            <h3 className="text-sm font-bold uppercase text-zinc-600 mb-2">{lang === "fr" ? "Utilisateur analysé" : "Analyzed user"}</h3>
            <div className="text-sm space-y-1">
              <div><strong>{result.target_user.email}</strong> ({result.target_user.name || "—"})</div>
              <div className="text-zinc-600">role={result.target_user.role || "—"} · org_role={result.target_user.org_role || "—"} · access_level={result.target_user.access_level || "—"}</div>
              {result.target_user.learning_groups?.length > 0 && (
                <div className="text-zinc-600">learning_groups: {result.target_user.learning_groups.join(", ")}</div>
              )}
              <div className="text-xs text-zinc-500 mt-2">
                Candidate groups calculés : <code className="bg-white px-1.5 py-0.5 rounded">{(result.computed.candidate_groups || []).join(", ") || "(aucun)"}</code>
              </div>
              <div className="text-xs text-zinc-500">is_designer = <strong>{result.computed.is_designer ? "OUI (voit tout)" : "Non"}</strong></div>
            </div>
          </div>

          {/* Score global */}
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-teal-200 bg-teal-50 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-teal-700" data-testid="visibility-visible-count">{result.visible_count}</div>
              <div className="text-xs text-teal-700">{lang === "fr" ? "Parcours visibles" : "Visible"}</div>
            </div>
            <div className="border border-rose-200 bg-rose-50 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-rose-700" data-testid="visibility-hidden-count">{result.total - result.visible_count}</div>
              <div className="text-xs text-rose-700">{lang === "fr" ? "Parcours cachés" : "Hidden"}</div>
            </div>
          </div>

          {/* Liste des parcours */}
          <div className="border border-zinc-200 rounded-xl overflow-hidden">
            <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 text-xs font-bold uppercase text-zinc-600">
              {lang === "fr" ? "Verdict par parcours" : "Per-pathway verdict"} ({result.total})
            </div>
            <div className="divide-y divide-zinc-100">
              {result.pathways.map((p) => (
                <div key={p.id} className="px-4 py-3 flex items-start gap-3" data-testid={`visibility-pathway-row-${p.id}`}>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${p.visible ? "bg-teal-500" : "bg-rose-500"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-zinc-900">{p.name}</div>
                    <div className="text-[11px] text-zinc-500">
                      type={p.pathway_type || "?"} · published={p.is_published ? "OUI" : "NON"}
                      {p.target_groups?.length > 0 && <> · target_groups=[{p.target_groups.join(", ")}]</>}
                    </div>
                    <div className={`text-xs mt-1 ${p.visible ? "text-teal-700" : "text-rose-700"}`}>
                      {p.visible ? "✓" : "✗"} {p.reason}
                    </div>
                  </div>
                </div>
              ))}
              {result.pathways.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-zinc-500">
                  {lang === "fr" ? "Aucun parcours dans cette organisation" : "No pathways in this organization"}
                </div>
              )}
            </div>
          </div>

          {/* Suggestions */}
          {result.total > result.visible_count && (
            <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 text-sm text-amber-900">
              <strong>💡 {lang === "fr" ? "Suggestions" : "Suggestions"}</strong>
              <ul className="mt-2 ml-4 list-disc space-y-1 text-xs">
                <li>{lang === "fr" ? "Si target_groups contient un libellé non standard (ex: \"Conseiller actif\"), ajoute-le dans la table de synonymes Knoria." : "If target_groups contains a non-standard label, add it to the Knoria synonyms table."}</li>
                <li>{lang === "fr" ? "Si is_published=NON, va dans l'éditeur du parcours et clique Publier." : "If is_published=NON, go to the pathway editor and click Publish."}</li>
                <li>{lang === "fr" ? "Si target_groups est vide, le parcours est ouvert à tous les rôles ayant accès au menu. Vérifie la Sélection des accès." : "If target_groups is empty, the pathway is open to all roles. Check Access Settings."}</li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
